import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  Paper,
  Stack,
  FormControlLabel,
  Checkbox,
  TextField,
  IconButton,
  Modal,
} from "@mui/material";
import { Add, Remove, Draw } from "@mui/icons-material";
import SignaturePad from "signature_pad";
import { useSnackbar } from "notistack";
import { useStoreDetails } from '../../../common/Providers/StoreDetailsProvider/UseStoreDetails';
import { useVehicleReservation } from '../../../common/Providers/VehicleReservationProvider/UseVehicleReservation';
import { generateSignedWaiverWithData } from '../../../common/PdfTemplates';
import { useStepper } from "../../../common/Providers/StepperProvider/useStepper";
import WaiverViewer from '../../WaiverViewer';
import StepperLayout from "./StepperLayout";
import SummaryCard from "../SummaryCard";
import RentalHeader from "../RentalHeader";
import FloatingActionButton from "../../FloatingActionButton";
import DriverSelection from "./DriverSelection";
import AdditionalDrivers, { AdditionalDriverSpec } from "./AdditionalDrivers";
import AgreementQuestions from "./AgreementQuestions";
import { useDrivingQuestionsConfig } from "./useDrivingQuestionsConfig";
import { useManageKeys } from "../../../common/Providers/ManageKeysProvider/UseManageKeys";

type UploadState = {
  has_uploaded_license: boolean;
  has_uploaded_insurance: boolean;
  has_signed_agreement: boolean;
};

type DocumentMetadata = {
  id: string;
  sort_order: number;
  is_main: boolean;
  document_type: string;
  owner: {
    id: string;
    type: string;
    name: string;
    identifier: string;
    email: string | null;
    phone: string | null;
  };
};

type FileWithMetadata = {
  file: File;
  metadata: DocumentMetadata;
};

const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

const AgreementStep: React.FC = () => {
  const { next, back, steps, goTo, navigateTo } = useStepper();
  const { setVehicleReservationValue, VehicleReservationMain } = useVehicleReservation();
  const { storeDetails, storeContent } = useStoreDetails();
  const { getKey } = useManageKeys();
  const { enqueueSnackbar } = useSnackbar();
  const drivingQuestionsConfig = useDrivingQuestionsConfig();

  const [data, setData] = useState<UploadState>({
    has_uploaded_license: false,
    has_uploaded_insurance: false,
    has_signed_agreement: false,
  });

  const [waiverPdfUrl, setWaiverPdfUrl] = useState<string>("/templates/waiver_template_with_fields.pdf");
  const [signatureModalOpen, setSignatureModalOpen] = useState<boolean>(false);

  const licensePreviewUrl = useRef<string | null>(null);
  const insurancePreviewUrl = useRef<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);

  const activeSteps = useMemo(() => {
    if (!storeDetails) return [];
    return [
      { key: "driving_questions", isRequired: drivingQuestionsConfig.questions.length > 0 },
      { key: "license", isRequired: storeDetails.is_license_required },
      { key: "insurance", isRequired: storeDetails.is_insurance_required },
      { key: "agreement", isRequired: storeDetails.is_agreement_required },
    ].filter(step => step.isRequired);
  }, [storeDetails, drivingQuestionsConfig.questions.length]);

  // Helper function to create document metadata
  const createDocumentMetadata = ({ ownerInfo, documentType, sortOrder = 0, isMain = false }: { ownerInfo: { id: string; type: string; name: string; identifier: string; email?: string | null; phone?: string | null }; documentType: string; sortOrder?: number; isMain?: boolean }): DocumentMetadata => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 11);
    const uniqueId = `${documentType}_${ownerInfo.identifier}_${timestamp}_${random}`.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    return {
      id: uniqueId,
      sort_order: sortOrder,
      is_main: isMain,
      document_type: documentType,
      owner: { ...ownerInfo, email: ownerInfo.email ?? null, phone: ownerInfo.phone ?? null }
    };
  };

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const currentStep = activeSteps[currentStepIndex]?.key;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === activeSteps.length - 1;

  useEffect(() => {
    if (signatureModalOpen) {
      // Small delay to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Clear any existing signature pad
        if (sigPadRef.current) {
          sigPadRef.current.off();
        }

        const resizeCanvas = () => {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const rect = canvas.getBoundingClientRect();
          canvas.width = rect.width * ratio;
          canvas.height = rect.height * ratio;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.scale(ratio, ratio);
            // Clear canvas with white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        };

        resizeCanvas();
        const pad = new SignaturePad(canvas, { penColor: "rgb(20,20,20)" });
        sigPadRef.current = pad;

        const handleResize = () => {
          const data = pad.toData();
          resizeCanvas();
          pad.fromData(data);
        };

        window.addEventListener("resize", handleResize);
        
        return () => {
          window.removeEventListener("resize", handleResize);
          pad.off();
        };
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [signatureModalOpen]);

  useEffect(() => {
    return () => {
      if (licensePreviewUrl.current) URL.revokeObjectURL(licensePreviewUrl.current);
      if (insurancePreviewUrl.current) URL.revokeObjectURL(insurancePreviewUrl.current);
    };
  }, []);

  const setFileWithPreview = (file: File | null, target: "license" | "insurance") => {
    if (!file) {
      if (target === "license") {
        if (licensePreviewUrl.current) URL.revokeObjectURL(licensePreviewUrl.current);
        licensePreviewUrl.current = null;
        setVehicleReservationValue("license_file", null);
      } else if (target === "insurance") {
        if (insurancePreviewUrl.current) URL.revokeObjectURL(insurancePreviewUrl.current);
        insurancePreviewUrl.current = null;
        setVehicleReservationValue("insurance_file", null);
      }
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      enqueueSnackbar(`File too large. Max ${MAX_FILE_MB} MB.`, { variant: "error" });
      return;
    }

    const url = URL.createObjectURL(file);

    // Create metadata for main driver
    const mainDriverName = VehicleReservationMain.name || "Main Driver";
    const metadata = createDocumentMetadata({
      ownerInfo: { id: "driver_0", type: "DRIVER", name: mainDriverName, identifier: "primary", email: VehicleReservationMain.email || null, phone: VehicleReservationMain.phone_number || null },
      documentType: target.toUpperCase(),
      sortOrder: 0,
      isMain: true
    });

    const fileWithMetadata: FileWithMetadata = { file, metadata };

    if (target === "license") {
      if (licensePreviewUrl.current) URL.revokeObjectURL(licensePreviewUrl.current);
      licensePreviewUrl.current = url;
      setVehicleReservationValue("license_file", fileWithMetadata);
    } else if (target === "insurance") {
      if (insurancePreviewUrl.current) URL.revokeObjectURL(insurancePreviewUrl.current);
      insurancePreviewUrl.current = url;
      setVehicleReservationValue("insurance_file", fileWithMetadata);
    }
  };

  const validateDrivingQuestions = () => {
    // Validate driver selection if it exists in config
    if (drivingQuestionsConfig.driverSelection) {
      if (VehicleReservationMain.is_only_driver === null) {
        enqueueSnackbar("Please select who will be driving.", { variant: "warning" });
        return false;
      }
      
      if (VehicleReservationMain.is_only_driver === false) {
        const additionalDrivers = VehicleReservationMain.additional_drivers || [];
        if (additionalDrivers.length === 0) {
          enqueueSnackbar("Please enter at least one additional driver.", { variant: "warning" });
          return false;
        }
        if (additionalDrivers.some((d: any) => !d.name.trim())) {
          enqueueSnackbar("Please fill in all driver names.", { variant: "warning" });
          return false;
        }
      }
    }
    
    // Validate required questions from config
    const requiredQuestions = drivingQuestionsConfig.questions.filter(q => q.required);
    for (const question of requiredQuestions) {
      // Check if question should be shown based on conditions
      const shouldShowQuestion = !question.showWhen || 
        VehicleReservationMain[question.showWhen.field as keyof typeof VehicleReservationMain] === question.showWhen.value;
      
      if (shouldShowQuestion && !VehicleReservationMain[question.id as keyof typeof VehicleReservationMain]) {
        const questionText = question.text.length > 50 
          ? question.text.substring(0, 50) + '...' 
          : question.text;
        enqueueSnackbar(`Please acknowledge: ${questionText}`, { variant: "warning" });
        return false;
      }
    }
    
    return true;
  };

  const validateStep = (stepKey: string): boolean => {
    switch (stepKey) {
      case 'driving_questions':
        return validateDrivingQuestions();
      
      case 'license':
        if (!storeDetails?.is_license_required) return true;
        
        if (!VehicleReservationMain.license_file) {
          enqueueSnackbar("Please upload your driver's license.", { variant: "warning" });
          return false;
        }
        
        return true;
      
      case 'insurance':
        if (!storeDetails?.is_insurance_required) return true;
        
        if (!VehicleReservationMain.insurance_file) {
          enqueueSnackbar("Please upload your insurance.", { variant: "warning" });
          return false;
        }
        
        return true;
      
      case 'agreement':
        if (!storeDetails?.is_agreement_required) return true;
        
        if (!VehicleReservationMain.agreement_file) {
          enqueueSnackbar("Please sign the waiver.", { variant: "warning" });
          return false;
        }
        
        return true;
      
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) {
      return;
    }
    setCurrentStepIndex((i) => Math.min(activeSteps.length - 1, i + 1));
  };
  const handlePrev = () => setCurrentStepIndex((i) => Math.max(0, i - 1));
  const handleClearSignature = () => sigPadRef.current?.clear();
  
  const handleCloseSignatureModal = () => {
    setSignatureModalOpen(false);
    if (sigPadRef.current) {
      sigPadRef.current.off();
      sigPadRef.current = null;
    }
  };
  
  const handleAttachSignature = async () => {
    if (sigPadRef.current?.isEmpty()) {
      enqueueSnackbar("Please sign first.", { variant: "warning" });
      return;
    }
    const signature = sigPadRef.current!.toDataURL("image/png");
    const renterName = VehicleReservationMain.name || "";
    const now = new Date();
    const signedAt = now.toISOString().slice(0, 16).replace('T', ' ');
    const pdfBlob = await generateSignedWaiverWithData(signature, {
      renterName,
      renterPhone: VehicleReservationMain.phone_number || "",
      renterEmail: VehicleReservationMain.email || "",
      signedByText: `Signed by ${renterName} on ${signedAt} GMT.`,
    });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setWaiverPdfUrl(pdfUrl);
    const agreementFile = new File([pdfBlob], "signed_waiver.pdf", { type: "application/pdf" });
    
    // Create metadata for agreement file
    const mainDriverName = VehicleReservationMain.name || "Main Driver";
    const agreementMetadata = createDocumentMetadata({
      ownerInfo: { id: "driver_0", type: "DRIVER", name: mainDriverName, identifier: "primary", email: VehicleReservationMain.email || null, phone: VehicleReservationMain.phone_number || null },
      documentType: "WAIVER",
      sortOrder: 0, // Sort order: main driver is always 0
      isMain: true
    });
    
    const fileWithMetadata: FileWithMetadata = {
      file: agreementFile,
      metadata: agreementMetadata
    };
    
    setVehicleReservationValue("agreement_file", fileWithMetadata);
    // setData(d => ({...d, has_signed_agreement: true}));
    enqueueSnackbar("Signature attached.", { variant: "success" });
    handleCloseSignatureModal();
  };

  const renderDrivingQuestionsStep = () => {
    const additionalDrivers = VehicleReservationMain.additional_drivers || [];

    const handleDriverSelectionChange = (value: boolean, showsAdditionalDrivers: boolean) => {
      setVehicleReservationValue("is_only_driver", value);
      if (!showsAdditionalDrivers) {
        setVehicleReservationValue("additional_drivers", null);
      }
    };

    const handleAdditionalDriversChange = (drivers: AdditionalDriverSpec[]) => {
      setVehicleReservationValue("additional_drivers", drivers);
    };

    return (
      <Box>
        {/* Driver Selection - Optional */}
        {drivingQuestionsConfig.driverSelection && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {drivingQuestionsConfig.driverSelection.prompt}
            </Typography>
            
            <DriverSelection
              config={drivingQuestionsConfig.driverSelection}
              currentValue={VehicleReservationMain.is_only_driver}
              onChange={handleDriverSelectionChange}
            />
          </>
        )}
        
        {/* Additional Drivers - Show if there are additional drivers */}
        {VehicleReservationMain.is_only_driver === false && (
          <AdditionalDrivers
            config={drivingQuestionsConfig}
            drivers={additionalDrivers}
            onChange={handleAdditionalDriversChange}
            mainDriverName={VehicleReservationMain.name}
            mainDriverEmail={VehicleReservationMain.email}
            fieldStyles={fieldStyles}
            onShowMessage={enqueueSnackbar}
          />
        )}
        
        {/* Agreement Questions - Filter based on conditions */}
        <AgreementQuestions
          questions={drivingQuestionsConfig.questions}
          reservationData={VehicleReservationMain}
          onChange={(field, value) => setVehicleReservationValue(field as keyof typeof VehicleReservationMain, value)}
        />
      </Box>
    );
  };

  const renderLicenseStep = () => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setFileWithPreview(file, "license");
      e.target.value = '';
    };
    
    const handleRemoveFile = () => {
      setFileWithPreview(null, "license");
    };
    
    const actualFile = VehicleReservationMain?.license_file instanceof File ? VehicleReservationMain.license_file : (VehicleReservationMain?.license_file as any)?.file || null;
    
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Your Driver's License</Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
              minWidth: 300,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Driver's License
            </Typography>

            {actualFile ? (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, mr: 1, wordBreak: "break-word" }}>
                  {actualFile.name} ({(actualFile.size / 1024).toFixed(1)} KB)
                </Typography>
                <Button variant="outlined" color="error" size="small" onClick={handleRemoveFile}>
                  Remove
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No file chosen
              </Typography>
            )}

            <input
              id="fileLicense"
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <label htmlFor="fileLicense">
              <Button variant="outlined" component="span">
                {actualFile ? "Change File" : "Choose File"}
              </Button>
            </label>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Accepted formats: PDF, JPEG, PNG (Max {MAX_FILE_MB}MB)
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderInsuranceStep = () => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setFileWithPreview(file, "insurance");
      e.target.value = '';
    };
    
    const handleRemoveFile = () => {
      setFileWithPreview(null, "insurance");
    };
    
    const actualFile = VehicleReservationMain?.insurance_file instanceof File ? VehicleReservationMain.insurance_file : (VehicleReservationMain?.insurance_file as any)?.file || null;
    
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Your Driver's Insurance</Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
              minWidth: 300,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Driver's Insurance
            </Typography>

            {actualFile ? (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, mr: 1, wordBreak: "break-word" }}>
                  {actualFile.name} ({(actualFile.size / 1024).toFixed(1)} KB)
                </Typography>
                <Button variant="outlined" color="error" size="small" onClick={handleRemoveFile}>
                  Remove
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No file chosen
              </Typography>
            )}

            <input
              id="fileInsurance"
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <label htmlFor="fileInsurance">
              <Button variant="outlined" component="span">
                {actualFile ? "Change File" : "Choose File"}
              </Button>
            </label>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Accepted formats: PDF, JPEG, PNG (Max {MAX_FILE_MB}MB)
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderWaiverStep = () => {
    return (
      <Box>
        {/* <Typography variant="h6" sx={{ mb: 1 }}>
          Waiver & eSign
        </Typography> */}
        {data.has_signed_agreement ? (
          <Alert severity="success">Waiver signed.</Alert>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <WaiverViewer waiverPdfUrl={waiverPdfUrl} />
              </Grid>

              <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Button 
                  variant="contained"
                  onClick={() => setSignatureModalOpen(true)}
                  sx={{ mb: 2, minWidth: 150 }}
                  startIcon={<Draw />}
                >
                  Sign
                </Button>
              </Grid>
            </Grid>

            <Modal
              open={signatureModalOpen}
              onClose={handleCloseSignatureModal}
              aria-labelledby="signature-modal-title"
              aria-describedby="signature-modal-description"
            >
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                maxWidth: '90vw',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Sign Waiver
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {getKey('please_sign_below_to_confirm_you_agree_to_the_terms_and_conditions', 'Please sign below to confirm you agree to the terms and conditions.')}
                </Typography>

                <canvas
                  ref={canvasRef}
                  style={{
                    width: "100%",
                    height: 280,
                    border: "1px solid #e0e0e0",
                    borderRadius: 4,
                  }}
                />
                
                <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                  <Button onClick={handleClearSignature}>Clear</Button>
                  <Button onClick={handleCloseSignatureModal}>Cancel</Button>
                  <Button variant="contained" onClick={handleAttachSignature}>
                    Attach Signature
                  </Button>
                </Stack>
              </Box>
            </Modal>
          </>
        )}
      </Box>
    );
  };

  const renderStepTitle = () => {
    switch (currentStep) {
      case "driving_questions":
        return "Driving Questions";
      case "license":
        return "Upload Documents (Driver's License)";
      case "insurance":
        return "Upload Documents (Insurance)";
      case "agreement":
        return getKey('upload_documents_waiver_esign', 'Upload Documents (Waiver & eSign)');
      default:
        return null;
    }
  };

  const onComplete = () => {
    // Validate all required steps before completing
    for (const step of activeSteps) {
      if (!validateStep(step.key)) {
        // Find the step index and navigate to it
        const stepIndex = activeSteps.findIndex(s => s.key === step.key);
        if (stepIndex !== -1) {
          setCurrentStepIndex(stepIndex);
        }
        return;
      }
    }
    next();
  };

  const handleHeaderActionClick = () => {
    const accessToken = localStorage.getItem("access-token");
    if (accessToken) {
      navigateTo(2); // Navigate by id
    } else {
      back();
    }
  };

  return (
      <Box>
        <StepperLayout
          rightContent={
            <SummaryCard
              isCheckoutStage={false}
              showDiscount={false}
            />
          }
        >
          <RentalHeader title="Carolina Rides" onActionClick={handleHeaderActionClick} primaryColor={storeContent.primary_color} />
          <Box sx={styles.contentContainer}>

            <Box sx={{ flex: 1, p: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>{renderStepTitle()}</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                {activeSteps.length === 0 && <Typography>No documents required.</Typography>}
                {currentStep === "driving_questions" && renderDrivingQuestionsStep()}
                {currentStep === "license" && renderLicenseStep()}
                {currentStep === "insurance" && renderInsuranceStep()}
                {currentStep === "agreement" && renderWaiverStep()}
              </Paper>
              {activeSteps.length > 1 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {activeSteps.map((step, i) => (
                      <Box key={step.key} sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: i === currentStepIndex ? "primary.main" : "grey.300" }} />
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {!isFirstStep && (
                      <Button variant="outlined" onClick={handlePrev}>Previous</Button>
                    )}
                    {!isLastStep && (
                      <Button variant="contained" onClick={handleNext}>Next</Button>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
      </StepperLayout>
      {/* Floating Continue Button - shown on the last step. Enforcement of all
          required uploads/signature lives in onComplete (validateStep), so the
          button no longer depends on agreement_file being present. */}
      {isLastStep && (
        <FloatingActionButton 
          label="Continue" 
          primaryColor={storeContent.primary_color} 
          onClick={onComplete} 
          visible={true} 
          disabled={false} 
        />
      )}   
    </Box>
  );
};

const fieldStyles = {
  "& .MuiInput-underline:before": {
    borderBottomColor: "#0047C0",
  },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottomColor: "#0047C0",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#0047C0",
  },
  "& .MuiInputBase-input": {
  },
};

const styles = {
  contentContainer: {
    flex: 1,
    p: 1.5,
    backgroundColor: "white",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  contentTitle: {
    color: "#333333",
    fontSize: "16px",
  },
  sectionTitle: {
    color: "#333333",
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
  }
};

export default AgreementStep;